import { CommandDialog, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDownIcon } from "@/assets";
import { Policy } from "@/types";
import { Filter } from "./SearchFilter.type";
import { SearchIcon, X } from "lucide-react";
import { isDefined } from "@/helper/utils";
import { useEffect, useMemo, useState } from "react";
import { usePolicies } from "@/modules/policies/hooks/use-policies";
import { formatPolicySummary } from "@/helper/policy";
import useToggle from "@/hooks/use-toggle";

type PoliciesFilterProps = {
  onChange: (input: Pick<Filter, "selectedPolicies">) => void;
  className?: string;
} & Pick<Filter, "selectedPolicies">;

export const PoliciesFilter = ({ selectedPolicies, onChange }: PoliciesFilterProps) => {
  const [showPolicyFiler, setShowPolicyFiler] = useToggle();
  const [inputValue, setInputValue] = useState("");
  const { policyList } = usePolicies();
  const [listOptions, setListOptions] = useState<Policy[]>([]);

  const selectedItems = useMemo(
    () =>
      policyList ? selectedPolicies.map((v) => policyList.find((opt) => opt.curbPolicyId === v)).filter(isDefined) : [],
    [selectedPolicies, policyList],
  );

  useEffect(() => {
    const requestId = window.setTimeout(() => {
      const term = inputValue.toLowerCase();
      const newOptions = policyList.filter(
        (p) =>
          selectedItems.indexOf(p) === -1 &&
          (p.name.toLowerCase().includes(term) || formatPolicySummary(p).toLowerCase().includes(term)),
      );
      setListOptions(newOptions);
    }, 500);

    return () => window.clearTimeout(requestId);
  }, [inputValue, selectedItems, policyList]);

  const removeSelectedItem = (value: string) => {
    const newValues = selectedPolicies.filter((val) => val !== value);
    onChange({ selectedPolicies: newValues });
  };

  const selectItem = (value: string) => {
    onChange({ selectedPolicies: [...selectedPolicies, value] });
  };

  const clearValues = () => {
    onChange({ selectedPolicies: [] });
  };

  return (
    <>
      <Button variant="outline" className="justify-between" onClick={setShowPolicyFiler}>
        Type de place
        <ChevronDownIcon className="transition duration-150 ease-in-out w-5" aria-hidden="true" />
      </Button>
      <CommandDialog open={showPolicyFiler} onOpenChange={setShowPolicyFiler}>
        <div>
          {selectedPolicies.length > 0 && (
            <div className="flex flex-wrap gap-1 p-4 max-h-[300px] scroll-py-1 overflow-y-auto">
              {selectedItems.map((item, index) => {
                const summary = formatPolicySummary(item);
                return (
                  <Badge
                    key={`selectedItem-${item.curbPolicyId}-${index}`}
                    variant="default"
                    className="max-w-full break-words whitespace-normal"
                  >
                    <span className="flex flex-col items-start">
                      <span>{item.name}</span>
                      {summary && <span className="text-[10px] leading-tight opacity-80">{summary}</span>}
                    </span>
                    <button
                      className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          removeSelectedItem(item.curbPolicyId);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => removeSelectedItem(item.curbPolicyId)}
                      type="button"
                    >
                      <X className="h-3 w-3 text-muted-foreground hover:text-red-500" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}
          <div className="flex h-9 items-center gap-2 border-b px-3">
            <SearchIcon className="size-4 shrink-0 opacity-50" />
            <input
              className="placeholder:text-muted-foreground flex h-10 w-full bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Search for a policy"
              onChange={(e) => setInputValue(e.target.value)}
              value={inputValue}
            />
          </div>
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {listOptions.map((item, idx) => {
                const summary = formatPolicySummary(item);
                return (
                  <CommandItem
                    key={`suggestion-${item.curbPolicyId}-${idx}`}
                    onSelect={() => selectItem(item.curbPolicyId)}
                    className="cursor-pointer"
                  >
                    <span className="flex flex-col">
                      <span>{item.name}</span>
                      {summary && <span className="text-xs text-muted-foreground">{summary}</span>}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {selectedPolicies.length > 0 && (
            <div className="border-t p-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  clearValues();
                }}
              >
                Clear selections
              </Button>
            </div>
          )}
        </div>
      </CommandDialog>
    </>
  );
};
